import React from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { Avatar, Box, Button, Card, Grid, Typography } from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';
import { useTranslation } from 'react-i18next';
import SecurityIcon from '@mui/icons-material/Security';
import VisibilityIcon from '@mui/icons-material/Visibility';
import PeopleIcon from '@mui/icons-material/People';
import GroupsIcon from '@mui/icons-material/Groups';
import EmailIcon from '@mui/icons-material/Email';
import FlagOutlinedIcon from '@mui/icons-material/FlagOutlined';
import TravelExploreOutlinedIcon from '@mui/icons-material/TravelExploreOutlined';
import HomeWorkOutlinedIcon from '@mui/icons-material/HomeWorkOutlined';
import ApartmentRoundedIcon from '@mui/icons-material/ApartmentRounded';
import ArrowForwardRoundedIcon from '@mui/icons-material/ArrowForwardRounded';
import SEO from '../components/SEO';
import {
  ACCENT,
  CheckList,
  CtaBanner,
  ctaWhiteButtonSx,
  Eyebrow,
  FeatureCard,
  HeroSubtitle,
  HeroTitle,
  IconTile,
  LandingHero,
  LandingSection,
  SectionHeading,
  tList,
} from './Landing/landingUi';

const valueIcons = [<SecurityIcon />, <VisibilityIcon />, <PeopleIcon />, <GroupsIcon />];

const initialsOf = (name = '') => {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (!parts.length) return '';
  const first = parts[0][0] || '';
  const last = parts.length > 1 ? parts[parts.length - 1][0] : '';
  return `${first}${last}`.toLocaleUpperCase('tr');
};

const ProductCard = ({ tag, title, description, points, cta, to, icon, highlight }) => {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  return (
    <Card
      component="article"
      sx={{
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        p: { xs: 3, md: 4 },
        ...(highlight && {
          borderColor: alpha(theme.palette.primary.main, 0.35),
          background: `linear-gradient(140deg, ${alpha(theme.palette.primary.main, isDark ? 0.16 : 0.07)}, ${alpha(ACCENT, isDark ? 0.1 : 0.03)})`,
        }),
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
        <IconTile size={54} radius="16px">
          {icon}
        </IconTile>
        <Box>
          <Typography component="p" sx={{ color: 'primary.main', fontWeight: 700, fontSize: '0.8rem', letterSpacing: '0.06em', textTransform: 'uppercase' }}>
            {tag}
          </Typography>
          <Typography component="h3" sx={{ fontWeight: 800, fontSize: '1.45rem', letterSpacing: '-0.01em' }}>
            {title}
          </Typography>
        </Box>
      </Box>
      <Typography sx={{ mt: 2, color: 'text.secondary', lineHeight: 1.7 }}>{description}</Typography>
      <Box sx={{ mt: 2.5 }}>
        <CheckList items={points} dense />
      </Box>
      <Box sx={{ flex: 1 }} />
      <Box sx={{ mt: 3 }}>
        <Button component={RouterLink} to={to} variant={highlight ? 'contained' : 'outlined'} endIcon={<ArrowForwardRoundedIcon />}>
          {cta}
        </Button>
      </Box>
    </Card>
  );
};

const About = () => {
  const theme = useTheme();
  const { t } = useTranslation();
  const isDark = theme.palette.mode === 'dark';

  const paragraphs = tList(t, 'about.story.paragraphs');
  const values = tList(t, 'about.values.items');
  const members = tList(t, 'about.team.members');

  return (
    <>
      <SEO title={t('about.seo.title')} description={t('about.seo.description')} />

      <LandingHero labelledBy="about-hero-title">
        <Box sx={{ textAlign: 'center', maxWidth: 820, mx: 'auto' }}>
          <Eyebrow>{t('about.hero.eyebrow')}</Eyebrow>
          <HeroTitle id="about-hero-title">{t('about.hero.title')}</HeroTitle>
          <HeroSubtitle sx={{ mx: 'auto', maxWidth: 680 }}>{t('about.hero.subtitle')}</HeroSubtitle>
        </Box>
      </LandingHero>

      <LandingSection tone="muted" labelledBy="about-story-title">
        <Grid container spacing={{ xs: 5, md: 8 }} alignItems="center">
          <Grid item xs={12} md={6}>
            <SectionHeading id="about-story-title" align="left" eyebrow={t('about.story.eyebrow')} title={t('about.story.title')} sx={{ mb: 3 }} />
            {paragraphs.map((text) => (
              <Typography key={text.slice(0, 24)} sx={{ color: 'text.secondary', fontSize: '1.03rem', lineHeight: 1.8, mb: 2 }}>
                {text}
              </Typography>
            ))}
          </Grid>
          <Grid item xs={12} md={6}>
            <Box sx={{ display: 'grid', gap: 2.5 }}>
              <FeatureCard icon={<FlagOutlinedIcon />} title={t('about.mission.title')} description={t('about.mission.description')} />
              <FeatureCard icon={<TravelExploreOutlinedIcon />} iconColor="success" title={t('about.vision.title')} description={t('about.vision.description')} />
            </Box>
          </Grid>
        </Grid>
      </LandingSection>

      <LandingSection labelledBy="about-products-title">
        <SectionHeading
          id="about-products-title"
          eyebrow={t('about.whatWeDo.eyebrow')}
          title={t('about.whatWeDo.title')}
          subtitle={t('about.whatWeDo.subtitle')}
        />
        <Box sx={{ display: 'grid', gap: 3, gridTemplateColumns: { xs: '1fr', md: 'repeat(2, minmax(0, 1fr))' } }}>
          <ProductCard
            tag={t('about.whatWeDo.platform.tag')}
            title={t('about.whatWeDo.platform.title')}
            description={t('about.whatWeDo.platform.description')}
            points={tList(t, 'about.whatWeDo.platform.points')}
            cta={t('about.whatWeDo.platform.cta')}
            to="/tanitim"
            icon={<HomeWorkOutlinedIcon />}
          />
          <ProductCard
            highlight
            tag={t('about.whatWeDo.crm.tag')}
            title={t('about.whatWeDo.crm.title')}
            description={t('about.whatWeDo.crm.description')}
            points={tList(t, 'about.whatWeDo.crm.points')}
            cta={t('about.whatWeDo.crm.cta')}
            to="/emlak-crm"
            icon={<ApartmentRoundedIcon />}
          />
        </Box>
      </LandingSection>

      <LandingSection tone="muted" labelledBy="about-values-title">
        <SectionHeading id="about-values-title" title={t('about.values.title')} subtitle={t('about.values.subtitle')} />
        <Box sx={{ display: 'grid', gap: 3, gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, minmax(0, 1fr))', md: 'repeat(4, minmax(0, 1fr))' } }}>
          {values.map((value, index) => (
            <FeatureCard key={value.title} icon={valueIcons[index % valueIcons.length]} title={value.title} description={value.description} />
          ))}
        </Box>
      </LandingSection>

      <LandingSection labelledBy="about-team-title">
        <SectionHeading id="about-team-title" title={t('about.team.title')} subtitle={t('about.team.description')} />
        <Box
          component="ul"
          sx={{
            listStyle: 'none',
            p: 0,
            m: 0,
            display: 'grid',
            gap: 3,
            gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, minmax(0, 1fr))', md: 'repeat(4, minmax(0, 1fr))' },
          }}
        >
          {members.map((member) => (
            <Box component="li" key={member.name}>
              <Card
                sx={{
                  height: '100%',
                  textAlign: 'center',
                  p: 3.5,
                  transition: 'transform 0.2s ease',
                  '&:hover': { transform: 'translateY(-4px)' },
                  '@media (prefers-reduced-motion: reduce)': { transition: 'none', '&:hover': { transform: 'none' } },
                }}
              >
                <Avatar
                  aria-hidden
                  sx={{
                    width: 96,
                    height: 96,
                    mx: 'auto',
                    mb: 2,
                    fontSize: '1.9rem',
                    fontWeight: 800,
                    color: '#fff',
                    background: `linear-gradient(135deg, ${theme.palette.primary.main}, ${ACCENT})`,
                    boxShadow: `0 12px 26px ${alpha(theme.palette.primary.main, isDark ? 0.35 : 0.25)}`,
                  }}
                >
                  {initialsOf(member.name)}
                </Avatar>
                <Typography component="h3" sx={{ fontWeight: 700, fontSize: '1.12rem' }}>
                  {member.name}
                </Typography>
                <Typography sx={{ mt: 0.5, color: 'primary.main', fontWeight: 600, fontSize: '0.95rem' }}>{member.role}</Typography>
                <Typography sx={{ mt: 1, color: 'text.secondary', fontSize: '0.92rem', lineHeight: 1.6 }}>{member.description}</Typography>
              </Card>
            </Box>
          ))}
        </Box>
      </LandingSection>

      <LandingSection labelledBy="about-contact-title" sx={{ pt: 0 }}>
        <CtaBanner headingId="about-contact-title" title={t('about.contact.title')} description={t('about.contact.description')}>
          <Button component={RouterLink} to="/contact" variant="contained" size="large" startIcon={<EmailIcon />} sx={{ ...ctaWhiteButtonSx, px: 4 }}>
            {t('about.contact.button')}
          </Button>
        </CtaBanner>
      </LandingSection>
    </>
  );
};

export default About;
